import { useEffect, useRef, useState } from 'react';

import * as S from './styles';
import { useGeocoder } from 'hooks/useGeocoder';
import { ISuperficialAddressProps } from 'interfaces/Address';
import { StandaloneSearchBox } from '@react-google-maps/api';
import { StyledInput } from 'components/Input/styles';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { lastLocationMarker } from 'components/Modals/OccurrenceManagementModal/Tabs/LastLocationsTab/LastLocationsMarker';

import { ReactComponent as FocusIcon } from "assets/auto-focus.svg";

type MapCoords = {
  lat: number;
  lng: number;
}

type Props = {
  label?: string;
  location?: MapCoords & {
    formattedAddress?: string;
  };
  readonly?: boolean;
  error?: string;
  onCoordsChange?: (coords: MapCoords) => void;
  onAddressChange?: (address: ISuperficialAddressProps) => void;
  onFormattedAddressChange?: (address: string) => void;
}

export function InputMap({
  label,
  location,
  readonly = false,
  error,
  onCoordsChange,
  onAddressChange,
  onFormattedAddressChange,
}: Props) {
  const [searchInput, setSearchInput] = useState<google.maps.places.SearchBox>();
  const [formattedAddress, setFormattedAddress] = useState<string>();
  const [position, setPosition] = useState<MapCoords>();

  const geocoder = useGeocoder();
  const mapRef = useRef<L.Map>(null);

  async function onLoad() {
    if (location) {
      const coordinates = {
        lat: location?.lat,
        lng: location?.lng,
      };

      setPosition(coordinates);

      if (location.formattedAddress) {
        setFormattedAddress(location.formattedAddress);
      }

      mapRef.current?.setView(coordinates, 15);
    }
  }

  async function handlePlaceChange() {
    const places = searchInput!.getPlaces();
    if (places) {
      const currentPlace = places[0];

      const coords = currentPlace.geometry?.location;
      if (coords && onCoordsChange) {
        onCoordsChange({
          lat: coords.lat(),
          lng: coords.lng(),
        })
      }

      const address = currentPlace.formatted_address;
      if (address && onFormattedAddressChange) {
        onFormattedAddressChange(address);
      }

      const addressComponents = currentPlace.address_components;
      if (addressComponents) {
        const response = await geocoder.formatGoogleComponentsToAddress(addressComponents);
        if (typeof response !== 'string' && onAddressChange) {
          onAddressChange(response);
        }
      }

      const location = {
        lat: currentPlace?.geometry?.location?.lat() || 0,
        lng: currentPlace?.geometry?.location?.lng() || 0,
      }

      setPosition(location);
      mapRef.current?.setView(location, 15);
    }
  }

  function handleFocusClick() {
    if (position) {
      mapRef?.current?.setView({
        lat: position.lat,
        lng: position.lng,
      }, 15)
    }
  }

  useEffect(() => {
    onLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <S.Map show={true}>
      {label && <S.Label>{label}</S.Label>}

      <S.StandaloneContainer>
        <StandaloneSearchBox 
          onLoad={(ref) => setSearchInput(ref)} 
          onPlacesChanged={handlePlaceChange}
        >
          <StyledInput 
            id="address" 
            className={`${readonly ? "readonly-address" : ""}`}
            placeholder="Digite um endereço"
            defaultValue={formattedAddress}
            readOnly={readonly}
            disabled={readonly}
            autoComplete="off"
          />
        </StandaloneSearchBox>
      </S.StandaloneContainer>

      <S.MapContainer>
        <MapContainer
          center={{
            lat: -23.496938,
            lng: -46.851398,
          }}
          scrollWheelZoom
          zoom={6}
          className="leaflet-map"
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {position && (
            <Marker title={formattedAddress} position={position} icon={lastLocationMarker()} />
          )}

          {position && (
            <S.MapFocusButton type="button" onClick={handleFocusClick}>
              <FocusIcon />
            </S.MapFocusButton>
          )}
        </MapContainer>
      </S.MapContainer>

      {error && <small>{error}</small>}
    </S.Map>
  )
}